// @flow
import type { ChildrenArray, Element } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type {
  SharedRSLChildren,
  SharedStatementChildren,
  SolutionChildren,
} from '../common-children-types.js';
import {
  sharedRSLChildrenElementTypes,
  sharedStatementChildrenChecker,
  solutionChildrenChecker,
} from '../common-children-types.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import React from 'react';
import { productColorSunflower } from '../../designSystem.js';
import { InternalHeading } from '../../InternalHeading.js';
import { RSLTNInstructionalSupports } from './RSLTNInstructionalSupports.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';

export function RSLTNTeacherLedExamples(props: {
  children: ChildrenArray<
    | Element<typeof RSLTNTeacherLedExamplesPreamble>
    | Element<typeof RSLTNTeacherLedExample>,
  >,
  _numPrecedingTeacherLedExamples: number,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNTeacherLedExamples,
  );

  if (failedValidation) return null;

  let exampleIndex = 0;
  const mappedChildren = React.Children.map(props.children, (child) => {
    if (child.type !== RSLTNTeacherLedExample) return child;
    const _child: React$Element<typeof RSLTNTeacherLedExample> = (child: any);
    return React.cloneElement(_child, {
      _index: props._numPrecedingTeacherLedExamples + exampleIndex++,
    });
  });

  return (
    <div>
      <InternalHeading level={3}>Examples</InternalHeading>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {mappedChildren}
      </div>
    </div>
  );
}
RSLTNTeacherLedExamples.spec = {
  children: childrenArrayMatcher(
    {
      min: 0,
      max: 1,
      elementType: t.elementType(RSLTNTeacherLedExamplesPreamble),
    },
    {
      min: 1,
      max: Infinity,
      elementType: t.elementType(RSLTNTeacherLedExample),
    },
  ),
};

export function RSLTNTeacherLedExamplesPreamble(props: {
  children: ChildrenArray<
    SharedRSLChildren | Element<typeof RSLTNInstructionalSupports>,
  >,
  ...CompilerInjectedProps,
}) {
  const { children, _performValidation } = props;
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLTNTeacherLedExamplesPreamble,
  );

  React.useEffect(() => {
    if (!_performValidation) return;
    if (failedValidation) return;

    const childrenList = React.Children.toArray(children);
    const supports = childrenList.filter(
      (child) => child.type.name === 'RSLTNInstructionalSupports',
    );
    if (supports.length === childrenList.length) {
      addErrors([
        {
          message:
            'RSLTNTeacherLedExamplesPreamble must include something other than RSLTNInstructionalSupports',
          ...locations.RSLTNTeacherLedExamplesPreamble,
        },
      ]);
    }
    if (supports.length > 1) {
      addErrors([
        {
          message:
            'RSLTNTeacherLedExamplesPreamble can only have one RSLTNInstructionalSupports',
          ...locations.RSLTNTeacherLedExamplesPreamble,
        },
      ]);
    } else if (
      supports.length === 1 &&
      last(childrenList).type.name !== 'RSLTNInstructionalSupports'
    ) {
      addErrors([
        {
          message:
            'RSLTNInstructionalSupports can only be included at the end of a RSLTNTeacherLedExamplesPreamble',
          ...locations.RSLTNTeacherLedExamplesPreamble,
        },
      ]);
    }
  });
  if (failedValidation) return null;
  return <div>{children}</div>;
}
RSLTNTeacherLedExamplesPreamble.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      ...sharedRSLChildrenElementTypes,
      t.elementType(RSLTNInstructionalSupports),
    ),
  ),
};

export function RSLTNTeacherLedExample(props: {
  _index: number,
  children: ChildrenArray<
    | Element<typeof RSLTNExampleStatement>
    | Element<typeof RSLTNTeacherLedExamplePart>
    | Element<typeof RSLTNExampleGuidance>
    | Element<typeof RSLTNInstructionalSupports>,
  >,
  ...CompilerInjectedProps,
}) {
  const { _index, children } = props;
  const [questionStatement, ...rest] = React.Children.toArray(children);
  const isMultiPart = rest[0].type === RSLTNTeacherLedExamplePart;

  const { failedValidation } = usePropsValidation(
    props,
    RSLTNTeacherLedExample,
  );
  if (failedValidation) return null;
  return (
    <div style={{ color: productColorSunflower.grey }}>
      <div
        style={{
          breakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <h4
          style={{
            fontFamily: 'Proxima Nova',
            fontWeight: 600,
            color: '#21374D',
            fontSize: 18,
            margin: 0,
          }}
        >
          Example {_index + 1}
        </h4>
        <div style={{ height: 16 }} />
        {questionStatement}
      </div>
      {isMultiPart
        ? // $FlowIssue bad cloneElement definition
          rest.map((child, i) => React.cloneElement(child, { _index: i }))
        : rest}
    </div>
  );
}
RSLTNTeacherLedExample.spec = {
  children: t.unionOfTypes(
    childrenArrayMatcher(
      { min: 1, max: 1, elementType: t.elementType(RSLTNExampleStatement) },
      {
        min: 2,
        max: 6,
        elementType: t.elementType(RSLTNTeacherLedExamplePart),
      },
      {
        min: 0,
        max: 1,
        elementType: t.elementType(RSLTNInstructionalSupports),
      },
    ),
    childrenArrayMatcher(
      { min: 1, max: 1, elementType: t.elementType(RSLTNExampleStatement) },
      { min: 1, max: 1, elementType: t.elementType(RSLTNExampleGuidance) },
      {
        min: 0,
        max: 1,
        elementType: t.elementType(RSLTNInstructionalSupports),
      },
    ),
  ),
};

export function RSLTNExampleStatement(props: {
  children: ChildrenArray<SharedStatementChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNExampleStatement);
  if (failedValidation) return null;
  return props.children;
}
RSLTNExampleStatement.spec = {
  children: sharedStatementChildrenChecker,
};

export function RSLTNExampleGuidance(props: {
  children: ChildrenArray<
    | Element<typeof RSLTNPurpose>
    | Element<typeof RSLTNSolution>
    | Element<typeof RSLTNExpectedMistakes>
    | Element<typeof RSLTNReflection>,
  >,
  ...CompilerInjectedProps,
}) {
  const children = React.Children.toArray(props.children);
  const { failedValidation } = usePropsValidation(props, RSLTNExampleGuidance);

  if (failedValidation) return null;
  return (
    <>
      <div
        style={{
          marginTop: 20,
          // TODO this is NOT a sunflower color, its a teacher-only
          // color from classic mathspace
          backgroundColor: '#F3F9FF',
          borderRadius: 8,
          padding: '24px 16px',
        }}
      >
        {interleave(<RSLTNExampleSectionDivider />, children)}
      </div>
    </>
  );
}
RSLTNExampleGuidance.spec = {
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(RSLTNPurpose) },
    { min: 1, max: 1, elementType: t.elementType(RSLTNSolution) },
    { min: 0, max: 1, elementType: t.elementType(RSLTNExpectedMistakes) },
    { min: 0, max: 1, elementType: t.elementType(RSLTNReflection) },
  ),
};

export function RSLTNTeacherLedExamplePart(props: {
  _index: number,
  children: ChildrenArray<
    | Element<typeof RSLTNSubexampleStatement>
    | Element<typeof RSLTNExampleGuidance>,
  >,
  ...CompilerInjectedProps,
}) {
  const [subproblemStatement, ...rest] = React.Children.toArray(props.children);

  const { failedValidation } = usePropsValidation(
    props,
    RSLTNTeacherLedExamplePart,
  );
  if (failedValidation) return null;
  return (
    <div style={{ marginTop: props._index === 0 ? 16 : 48 }}>
      <div>
        {/* $FlowIssue cloneElement signature seems dodgy */}
        {React.cloneElement(subproblemStatement, { _index: props._index })}
      </div>
      {rest}
    </div>
  );
}
RSLTNTeacherLedExamplePart.spec = {
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(RSLTNSubexampleStatement) },
    { min: 1, max: 1, elementType: t.elementType(RSLTNExampleGuidance) },
  ),
};

export function RSLTNPurpose(props: {
  children: ChildrenArray<SharedRSLChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNPurpose);

  if (failedValidation) return null;
  return (
    <RSTNInternalExampleSection label="Purpose">
      {props.children}
    </RSTNInternalExampleSection>
  );
}
// Although at the correpsonding level to the worked question 'stages'
// of the RSL the Purpose section has much more in common with the general
// explain-y, pedegogical sections, e.g. we don't need tech screenshots here.
// Accordingly we use the `sharedRSLChildrenCheker` here.
RSLTNPurpose.spec = {
  children: t.unionOfTypes(...sharedRSLChildrenElementTypes),
};

export function RSLTNSolution(props: {
  children: ChildrenArray<SolutionChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNSolution);

  if (failedValidation) return null;
  return (
    <RSTNInternalExampleSection label="Solution">
      {props.children}
    </RSTNInternalExampleSection>
  );
}
RSLTNSolution.spec = { children: solutionChildrenChecker };

// The expected mistakes represent a possible incorrect solution path
// a student might take, so it makes the most sense to use SolutionChildren
export function RSLTNExpectedMistakes(props: {
  children: ChildrenArray<SolutionChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNExpectedMistakes);

  if (failedValidation) return null;
  return (
    <RSTNInternalExampleSection label="Expected mistakes">
      {props.children}
    </RSTNInternalExampleSection>
  );
}
RSLTNExpectedMistakes.spec = { children: solutionChildrenChecker };

export function RSLTNReflection(props: {
  children: ChildrenArray<SharedRSLChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNReflection);

  if (failedValidation) return null;
  return (
    <RSTNInternalExampleSection label="Reflecting with students">
      {props.children}
    </RSTNInternalExampleSection>
  );
}
RSLTNReflection.spec = {
  children: t.childrenArray(t.unionOfTypes(...sharedRSLChildrenElementTypes)),
};

export function RSLTNSubexampleStatement(props: {
  _index: number,
  children: ChildrenArray<SharedStatementChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNSubexampleStatement,
  );

  if (failedValidation) return null;
  return (
    <div style={{ display: 'flex', breakInside: 'avoid' }}>
      <div
        style={{
          color: productColorSunflower.grey90,
          borderRadius: 4,
          fontWeight: 600,
          fontSize: 16,
        }}
      >
        {String.fromCharCode(97 + props._index)}
      </div>
      <div style={{ width: 8, flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {props.children}
      </div>
    </div>
  );
}
RSLTNSubexampleStatement.spec = { children: sharedStatementChildrenChecker };

function RSTNInternalExampleSection(props: {
  label: string,
  children: ChildrenArray<SharedRSLChildren | SolutionChildren>,
}) {
  const { label, children } = props;
  return (
    <div style={{ breakInside: 'avoid' }}>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function RSLTNExampleSectionDivider() {
  return <div style={{ height: 24 }} />;
}
function interleave<T: React$Element<*>>(e: T, es: T[]) {
  if (es.length === 0) return [];
  const result = [es.shift()];
  while (es[0] !== undefined) {
    result.push(e);
    result.push(es.shift());
  }
  // NOTE poorly chosen numeric keys at the call site can cause issues here...
  const withKeys = result.map<T>((e, i) =>
    React.cloneElement(e, { key: e.key || i }),
  );
  return withKeys;
}

function last<T>(xs: Array<T>): T {
  return xs[xs.length - 1];
}
